// Scenes & INDEX panel guide — the left rail that lists every scene in the course.
// Locale-keyed like every guide (content.<lang> → { feature, title, sections }).

const scenesGuide = {
  id: "scenes",
  icon: "view_carousel",
  keywords: [
    "scene",
    "sahne",
    "slide",
    "slayt",
    "index",
    "içindekiler",
    "sahneler",
    "section",
    "bölüm",
    "add scene",
    "sahne ekle",
    "duplicate",
    "çoğalt",
    "delete",
    "sil",
    "rename",
    "yeniden adlandır",
    "reorder",
    "sırala",
    "hide",
    "gizle",
    "saved slides",
    "saved templates",
    "kayıtlı slaytlar",
    "kayıtlı taslaklarım",
    "save as template",
    "taslak olarak kaydet",
    "multi-select",
    "çoklu seçim",
    "bulk delete",
    "toplu silme",
    "copy scene",
    "sahneyi kopyala",
    "paste scene",
    "sahne yapıştır",
    "total duration",
    "toplam süre",
    "contained views",
    "işaret içeriği",
    "escena",
    "escenas",
    "diapositiva",
    "índice",
    "sección",
    "añadir escena",
    "duplicar",
    "eliminar",
    "renombrar",
    "reordenar",
    "ocultar",
    "plantillas guardadas",
    "guardar como plantilla",
    "selección múltiple",
    "copiar escena",
    "pegar escena",
    "duración total",
    "vistas contenidas",
  ],
  content: {
    en: {
      feature: "Scenes",
      title: "Scenes & the INDEX panel",
      sections: [
        {
          id: "overview",
          title: "What the INDEX panel is",
          videoUrl: null,
          text:
            "Click **Scenes** at the top-left of the editor to open the scene panel.\n\n" +
            "- The training's title sits at the top; click it to rename the training.\n" +
            "- **INDEX** lists every scene, top to bottom, in the exact order they play for the learner. Scenes are numbered (1, 2, 2.1 …); a section can be collapsed with its arrow. The INDEX header also shows the total duration of the visible scenes.\n" +
            "- **CONTAINED VIEWS**, below the INDEX, lists views that only open from a mark elsewhere in the course (a linked popup or overlay) rather than playing in the main flow.\n" +
            "- Click the INDEX or CONTAINED VIEWS header to collapse or expand that list. If the course has scored questions, the **Total Score** appears above the INDEX.\n\n" +
            "Click a scene to open it on the canvas.",
        },
        {
          id: "manage-scenes",
          title: "Add, duplicate, show/hide, delete",
          videoUrl: null,
          text:
            "The button bar at the bottom of the panel acts on the selected scene, section or contained view. From left to right:\n\n" +
            "- **Create new slide** — opens **Template Selection**: start from an empty scene or a template (**From Template**), or import a recording (**Screen Training**).\n" +
            "- **Create new section** — adds a section (a folder that groups scenes) with a blank scene already inside it.\n" +
            "- **Saved Templates** — see the section below.\n" +
            "- **Duplicate view** — copies the selected scene, including its narration, right after it (not available for sections or contained views).\n" +
            "- The **eye** button hides or shows the scene for learners without removing it. Every scene row also has its own eye, and hidden scenes are shown struck through. At least one scene must stay visible.\n" +
            '- **Delete** — removes the selected scene, section or contained view after a confirmation; for a scene it shows the scene number, e.g. "Scene 3". The last remaining scene can\'t be deleted.\n\n' +
            "When several scenes are selected, Delete removes all of them at once (see Select several scenes).",
        },
        {
          id: "organize",
          title: "Rename & reorder",
          videoUrl: null,
          text:
            "- Click a scene's title once to select it, then click again (or double-click) to rename it in place. Press Enter to save or Esc to cancel.\n" +
            "- Drag a scene up or down to reorder it, or drop it onto a section to nest it inside.\n" +
            "- The order shown in the INDEX is the order the course plays in — reordering here changes the playback order for the learner.",
        },
        {
          id: "saved-slides",
          title: "Saved Templates",
          videoUrl: null,
          text:
            "The third button in the bar (the library icon) opens **Saved Templates** — scenes you saved earlier for reuse.\n\n" +
            "- To save the open scene, open **Settings** in the right menu with nothing selected and press **Save as Template**.\n" +
            "- In **Saved Templates**, select a scene and press **Use** (or double-click it) to add a copy at the end of the INDEX.\n" +
            "- Not available while a contained view is selected.",
        },
        {
          id: "multi-select",
          title: "Select several scenes",
          videoUrl: null,
          text:
            "Work on many scenes at once.\n\n" +
            "- Ctrl+click (⌘+click on Mac) adds a scene to the selection or removes it.\n" +
            "- Shift+click selects every scene between the last clicked one and this one.\n" +
            "- Drag any selected scene to move the whole selection together; it's a single undo step.\n" +
            '- **Delete** asks for confirmation ("Delete 4 scenes?") and removes them all. It can\'t remove every scene in the training.\n' +
            "- Press Esc to clear the selection.",
        },
        {
          id: "copy-scene",
          title: "Copy a scene to another training",
          videoUrl: null,
          text:
            "Move a finished scene between trainings without rebuilding it.\n\n" +
            "- Open the scene, make sure no element is selected, and press **Copy scene** in the **Scene** card of the right menu (or press Ctrl+C).\n" +
            "- Open the target training — or stay in this one — and press Ctrl+V. A confirmation shows what the scene contains; press **Add scene**.\n" +
            "- The scene is inserted right after the scene you have open. When it comes from another training, its media is copied into this one.",
        },
      ],
    },
    tr: {
      feature: "Sahneler",
      title: "Sahneler ve SAHNELER paneli",
      sections: [
        {
          id: "overview",
          title: "SAHNELER paneli nedir",
          videoUrl: null,
          text:
            "Sahne panelini açmak için editörün sol üstündeki **Sahneler**'e tıklayın.\n\n" +
            "- Eğitimin başlığı en üstte yer alır; eğitimi yeniden adlandırmak için başlığa tıklayın.\n" +
            "- **SAHNELER** listesi, kurstaki tüm sahneleri öğrenciye oynatılacakları sırayla yukarıdan aşağıya gösterir. Sahneler numaralıdır (1, 2, 2.1 …); bir bölüm okuyla daraltılabilir. Liste başlığında görünür sahnelerin toplam süresi de yer alır.\n" +
            "- Altındaki **İŞARET İÇERİĞİ**, ana akışta oynatılmayan, kursun başka bir yerindeki bir işaretten açılan görünümleri (bağlantılı açılır pencere veya katman) listeler.\n" +
            "- Listeyi daraltmak veya genişletmek için SAHNELER ya da İŞARET İÇERİĞİ başlığına tıklayın. Kursta puanlı sorular varsa listenin üstünde **Toplam Puan** görünür.\n\n" +
            "Bir sahneyi tuvalde açmak için üzerine tıklayın.",
        },
        {
          id: "manage-scenes",
          title: "Ekleme, çoğaltma, gösterme/gizleme, silme",
          videoUrl: null,
          text:
            "Panelin altındaki düğme çubuğu seçili sahne, bölüm veya işaret içeriği üzerinde çalışır. Soldan sağa:\n\n" +
            "- **Yeni Sahne Oluştur** — **Şablon Seçimi**'ni açar: boş bir sahneyle ya da bir şablonla başlayın (**Şablondan**) veya bir kaydı içe aktarın (**Ekran Eğitimi**).\n" +
            "- **Yeni Bölüm Oluştur** — içinde boş bir sahneyle birlikte bir bölüm (sahneleri gruplayan bir klasör) ekler.\n" +
            "- **Kayıtlı Taslaklarım** — aşağıdaki bölüme bakın.\n" +
            "- **Sahneyi Kopyala** — seçili sahneyi anlatımıyla birlikte kopyalayıp hemen ardına ekler (bölümler ve işaret içerikleri için kullanılamaz).\n" +
            "- **Göz** düğmesi sahneyi silmeden öğrenciler için gizler veya gösterir. Her sahne satırının da kendi göz düğmesi vardır; gizli sahnelerin üstü çizili görünür. En az bir sahne görünür kalmalıdır.\n" +
            '- **Sil** — seçili sahneyi, bölümü veya işaret içeriğini bir onaydan sonra kaldırır; bir sahne için onayda sahne numarası gösterilir, ör. "Sahne 3". Kalan son sahne silinemez.\n\n' +
            "Birden fazla sahne seçiliyken Sil hepsini tek seferde kaldırır (bkz. Birden fazla sahne seçme).",
        },
        {
          id: "organize",
          title: "Yeniden adlandırma ve sıralama",
          videoUrl: null,
          text:
            "- Bir sahnenin başlığına önce seçmek için tıklayın, ardından yeniden adlandırmak için tekrar (veya çift) tıklayın. Kaydetmek için Enter'a, vazgeçmek için Esc'ye basın.\n" +
            "- Bir sahneyi yukarı veya aşağı sürükleyerek sırasını değiştirin ya da bir bölümün üzerine bırakarak içine yerleştirin.\n" +
            "- SAHNELER listesinde gördüğünüz sıra, kursun oynatılma sırasıdır — burada sırayı değiştirmek öğrencinin göreceği sırayı da değiştirir.",
        },
        {
          id: "saved-slides",
          title: "Kayıtlı Taslaklarım",
          videoUrl: null,
          text:
            "Düğme çubuğundaki üçüncü düğme (kitaplık simgesi) **Kayıtlı Taslaklarım** penceresini açar — daha önce yeniden kullanmak için kaydettiğiniz sahneler.\n\n" +
            "- Açık sahneyi kaydetmek için hiçbir şey seçili değilken sağ menüde **Ayarlar**'ı açın ve **Taslak Olarak Kaydet**'e basın.\n" +
            "- **Kayıtlı Taslaklarım** içinde bir sahne seçip **Kullan**'a basın (veya çift tıklayın); bir kopyası SAHNELER listesinin sonuna eklenir.\n" +
            "- Bir işaret içeriği seçiliyken kullanılamaz.",
        },
        {
          id: "multi-select",
          title: "Birden fazla sahne seçme",
          videoUrl: null,
          text:
            "Birçok sahne üzerinde aynı anda çalışın.\n\n" +
            "- Ctrl+tıklama (Mac'te ⌘+tıklama) bir sahneyi seçime ekler veya seçimden çıkarır.\n" +
            "- Shift+tıklama, son tıklanan sahne ile bu sahne arasındaki tüm sahneleri seçer.\n" +
            "- Seçili sahnelerden herhangi birini sürükleyerek tüm seçimi birlikte taşıyın; bu işlem tek adımda geri alınır.\n" +
            '- **Sil** onay ister ("4 sahne silinsin mi?") ve hepsini kaldırır. Eğitimdeki tüm sahneleri silemez.\n' +
            "- Seçimi temizlemek için Esc'ye basın.",
        },
        {
          id: "copy-scene",
          title: "Sahneyi başka bir eğitime kopyalama",
          videoUrl: null,
          text:
            "Hazır bir sahneyi yeniden oluşturmadan eğitimler arasında taşıyın.\n\n" +
            "- Sahneyi açın, hiçbir öğenin seçili olmadığından emin olun ve sağ menüdeki **Sahne** kartında **Sahneyi kopyala**'ya basın (veya Ctrl+C'ye basın).\n" +
            "- Hedef eğitimi açın — ya da bu eğitimde kalın — ve Ctrl+V'ye basın. Açılan onay penceresi sahnenin içeriğini gösterir; **Sahneyi ekle**'ye basın.\n" +
            "- Sahne, açık olan sahnenin hemen ardına eklenir. Başka bir eğitimden geliyorsa medyası bu eğitime kopyalanır.",
        },
      ],
    },
    es: {
      feature: "Escenas",
      title: "Escenas y el panel ÍNDICE",
      sections: [
        {
          id: "overview",
          title: "Qué es el panel ÍNDICE",
          videoUrl: null,
          text:
            "Haz clic en **Escenas**, en la parte superior izquierda del editor, para abrir el panel de escenas.\n\n" +
            "- El título de la formación aparece arriba; haz clic en él para cambiar el nombre de la formación.\n" +
            "- **ÍNDICE** muestra todas las escenas, de arriba abajo, en el orden exacto en que se reproducen para el alumno. Las escenas están numeradas (1, 2, 2.1 …); una sección se puede contraer con su flecha. La cabecera del ÍNDICE también muestra la duración total de las escenas visibles.\n" +
            "- **VISTAS CONTENIDAS**, debajo del ÍNDICE, muestra las vistas que solo se abren desde una marca situada en otra parte del curso (una ventana emergente o una superposición enlazada), en lugar de reproducirse en el flujo principal.\n" +
            "- Haz clic en la cabecera ÍNDICE o VISTAS CONTENIDAS para contraer o desplegar esa lista. Si el curso tiene preguntas puntuadas, la **Puntuación Total** aparece encima del ÍNDICE.\n\n" +
            "Haz clic en una escena para abrirla en el lienzo.",
        },
        {
          id: "manage-scenes",
          title: "Añadir, duplicar, mostrar/ocultar y eliminar",
          videoUrl: null,
          text:
            "La barra de botones de la parte inferior del panel actúa sobre la escena, la sección o la vista contenida seleccionada. De izquierda a derecha:\n\n" +
            "- **Crear nueva diapositiva** — abre **Selección de plantilla**: empieza con una escena vacía o con una plantilla (**Desde Plantilla**), o importa una grabación (**Formación en pantalla**).\n" +
            "- **Crear Nueva Sección** — añade una sección (una carpeta que agrupa escenas) que ya contiene una escena en blanco.\n" +
            "- **Plantillas Guardadas** — consulta la sección de abajo.\n" +
            "- **Duplicar Vista** — copia la escena seleccionada, incluida su narración, justo después de ella (no disponible para secciones ni vistas contenidas).\n" +
            "- El botón del **ojo** oculta o muestra la escena a los alumnos sin eliminarla. Cada fila de escena tiene también su propio ojo, y las escenas ocultas se muestran tachadas. Al menos una escena debe permanecer visible.\n" +
            '- **Eliminar** — quita la escena, la sección o la vista contenida seleccionada tras una confirmación; para una escena muestra su número, p. ej. "Escena 3". La última escena que queda no se puede eliminar.\n\n' +
            "Cuando hay varias escenas seleccionadas, Eliminar las quita todas a la vez (consulta Seleccionar varias escenas).",
        },
        {
          id: "organize",
          title: "Renombrar y reordenar",
          videoUrl: null,
          text:
            "- Haz clic una vez en el título de una escena para seleccionarla y vuelve a hacer clic (o doble clic) para renombrarla en el mismo lugar. Pulsa Enter para guardar o Esc para cancelar.\n" +
            "- Arrastra una escena hacia arriba o hacia abajo para reordenarla, o suéltala sobre una sección para anidarla dentro.\n" +
            "- El orden que se muestra en el ÍNDICE es el orden en que se reproduce el curso: reordenar aquí cambia el orden de reproducción para el alumno.",
        },
        {
          id: "saved-slides",
          title: "Plantillas Guardadas",
          videoUrl: null,
          text:
            "El tercer botón de la barra (el icono de biblioteca) abre **Plantillas Guardadas**: escenas que guardaste antes para reutilizarlas.\n\n" +
            "- Para guardar la escena abierta, abre **Ajustes** en el menú derecho sin nada seleccionado y pulsa **Guardar como Plantilla**.\n" +
            "- En **Plantillas Guardadas**, selecciona una escena y pulsa **Usar** (o haz doble clic en ella) para añadir una copia al final del ÍNDICE.\n" +
            "- No está disponible mientras hay una vista contenida seleccionada.",
        },
        {
          id: "multi-select",
          title: "Seleccionar varias escenas",
          videoUrl: null,
          text:
            "Trabaja con muchas escenas a la vez.\n\n" +
            "- Ctrl+clic (⌘+clic en Mac) añade una escena a la selección o la quita.\n" +
            "- Shift+clic selecciona todas las escenas entre la última en la que hiciste clic y esta.\n" +
            "- Arrastra cualquier escena seleccionada para mover toda la selección a la vez; se deshace en un solo paso.\n" +
            '- **Eliminar** pide confirmación ("¿Eliminar 4 escenas?") y las quita todas. No puede quitar todas las escenas de la formación.\n' +
            "- Pulsa Esc para borrar la selección.",
        },
        {
          id: "copy-scene",
          title: "Copiar una escena a otra formación",
          videoUrl: null,
          text:
            "Lleva una escena terminada de una formación a otra sin tener que volver a crearla.\n\n" +
            "- Abre la escena, asegúrate de que no hay ningún elemento seleccionado y pulsa **Copiar escena** en la tarjeta **Escena** del menú derecho (o pulsa Ctrl+C).\n" +
            "- Abre la formación de destino (o quédate en esta) y pulsa Ctrl+V. Una confirmación muestra lo que contiene la escena; pulsa **Añadir escena**.\n" +
            "- La escena se inserta justo después de la escena que tienes abierta. Cuando procede de otra formación, sus medios se copian a esta.",
        },
      ],
    },
  },
};

export default scenesGuide;
